import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Directionality } from '@angular/cdk/bidi';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, of, shareReplay } from 'rxjs';
import { LOCAL_STORAGE } from '@core/storage/storage.tokens';
import { LOCAL_KEYS } from '@core/storage/storage.keys';
import { DEFAULT_LANGUAGE, switchLanguage, type AppLanguage } from './i18n.config';

/**
 * Applies the stored (or default) language while the app boots.
 *
 * This has to complete before the first API request goes out: the auth interceptor
 * translates 401/403 toasts with `TranslateService.instant()`, which returns the raw
 * key (`auth.errors.session_expired`) when no translation file has been loaded yet.
 */
@Injectable({ providedIn: 'root' })
export class AppLanguageService {
  private translate = inject(TranslateService);
  private doc = inject(DOCUMENT);
  private storage = inject(LOCAL_STORAGE);
  private directionality = inject(Directionality);
  private ready?: Observable<unknown>;

  /**
   * Idempotent — every caller shares the same in-flight load, so app initializers can
   * chain off it to guarantee they run after translations are in place.
   */
  initialize(): Observable<unknown> {
    this.ready ??= switchLanguage(
      this.translate,
      this.doc,
      this.storage,
      this.directionality,
      this.initialLanguage(),
    ).pipe(
      // A failed translation file must not stop the app from booting.
      catchError((err) => (console.error('Failed to load translations', err), of(null))),
      shareReplay(1),
    );
    return this.ready;
  }

  private initialLanguage(): AppLanguage {
    return (this.storage.get(LOCAL_KEYS.language) as AppLanguage) ?? DEFAULT_LANGUAGE;
  }
}
